import { useCallback, useRef, useEffect } from "react";
import { useTonConnectUI } from "@tonconnect/ui-react";
import type { Wallet as TonConnectWallet } from "@tonconnect/ui-react";
import { useRequestGenericTonProofChallengeMutation } from "../api/wallets.service";
import { isHttpError } from "../../../shared/api/http";

interface ChallengeData {
  challengeId: string;
  payload: string;
  expiresAt: number;
}

interface UseProofChallengeParams {
  tonWallet: TonConnectWallet | null;
}

/**
 * Hook to manage TON proof challenge generation and lifecycle
 * 
 * Responsibilities:
 * - Generate challenge from backend before wallet connection
 * - Cache and reuse valid challenges
 * - Set challenge in TonConnect UI for proof request
 * - Handle 401 errors with backoff
 */
export function useProofChallenge({ tonWallet }: UseProofChallengeParams) {
  const [tonConnectUI] = useTonConnectUI();
  const { mutateAsync: requestChallenge } = useRequestGenericTonProofChallengeMutation();
  
  const challengeRef = useRef<ChallengeData | null>(null);
  const isGeneratingRef = useRef(false);
  const last401ErrorRef = useRef<number>(0);
  const previousWalletRef = useRef(tonWallet);
  const publishedRequestRef = useRef<{
    ui: typeof tonConnectUI;
    payload: string | null;
  } | null>(null);

  const publishProof = useCallback((payload: string | null) => {
    const published = publishedRequestRef.current;
    if (published?.ui === tonConnectUI && published.payload === payload) return;

    // A new ready object makes TonConnect regenerate the connection link and
    // abandon its previous session, even when tonProof has the same value.
    tonConnectUI.setConnectRequestParameters(payload === null
      ? { state: 'loading' }
      : { state: 'ready', value: { tonProof: payload } });
    publishedRequestRef.current = { ui: tonConnectUI, payload };
  }, [tonConnectUI]);

  const recreateProofPayload = useCallback(async () => {
    console.log("[useProofChallenge] recreateProofPayload called", {
      hasTonWallet: !!tonWallet,
      isGenerating: isGeneratingRef.current,
    });

    if (tonWallet) {
      console.log("[useProofChallenge] Wallet already connected, skipping");
      return;
    }

    // Prevent multiple simultaneous requests
    if (isGeneratingRef.current) {
      console.log("[useProofChallenge] Already generating, skipping");
      return;
    }

    // If we got a 401 error recently (within last 30 seconds), don't retry to prevent spam
    const now = Date.now();
    if (last401ErrorRef.current > 0 && (now - last401ErrorRef.current) < 30000) {
      console.warn("[useProofChallenge] Skipping challenge generation - recent 401 error");
      return;
    }

    // If we already have a valid challenge, reuse it
    const nowSeconds = Math.floor(now / 1000);
    if (challengeRef.current && challengeRef.current.expiresAt > nowSeconds + 60) {
      // Challenge is still valid (more than 60 seconds until expiry)
      console.log("[useProofChallenge] Reusing existing challenge", {
        challengeId: challengeRef.current.challengeId,
        expiresAt: challengeRef.current.expiresAt,
        timeUntilExpiry: challengeRef.current.expiresAt - nowSeconds,
      });
      publishProof(challengeRef.current.payload);
      return;
    }

    isGeneratingRef.current = true;
    console.log("[useProofChallenge] Starting challenge generation");

    publishProof(null);

    try {
      console.log("[useProofChallenge] Requesting challenge from backend");
      const challenge = await requestChallenge();
      console.log("[useProofChallenge] Challenge received", {
        challengeId: challenge.challengeId,
        payload: challenge.payload,
        expiresAt: challenge.expiresAt,
      });

      // Reset 401 error timestamp on success
      last401ErrorRef.current = 0;
      challengeRef.current = {
        challengeId: challenge.challengeId,
        payload: challenge.payload,
        expiresAt: challenge.expiresAt,
      };

      // Set the connect request parameters with ton_proof payload
      console.log("[useProofChallenge] Setting connect request parameters with proof");
      publishProof(challenge.payload);
      console.log("[useProofChallenge] Connect request parameters set successfully");
    } catch (error) {
      console.error("[useProofChallenge] Failed to generate challenge", error);
      // Check if it's a 401 error
      const is401 = isHttpError(error) && error.status === 401 ||
                    (error && typeof error === 'object' && 'status' in error && error.status === 401);
      if (is401) {
        // Record the timestamp of the 401 error to prevent spam
        last401ErrorRef.current = Date.now();
        console.warn("[useProofChallenge] Got 401 error - will not retry for 30 seconds");
      }
      publishProof(null);
      challengeRef.current = null;
    } finally {
      isGeneratingRef.current = false;
      console.log("[useProofChallenge] Challenge generation finished");
    }
  }, [publishProof, tonWallet, requestChallenge]);

  // Retry failed requests and refresh expiring challenges even when no render
  // occurs. Keep the callback stable across mutation state updates.
  useEffect(() => {
    const wasConnected = previousWalletRef.current !== null;
    previousWalletRef.current = tonWallet;
    if (tonWallet) return;
    if (wasConnected) {
      // A previous proof may have been consumed, and reconnect recovery can
      // put TonConnect in loading state outside this hook.
      challengeRef.current = null;
      publishedRequestRef.current = null;
    }
    if (!challengeRef.current ||
        challengeRef.current.expiresAt <= Math.floor(Date.now() / 1000) + 60 ||
        publishedRequestRef.current?.ui !== tonConnectUI) {
      publishProof(null);
    }
    const timeoutId = setTimeout(() => { void recreateProofPayload(); }, 100);
    const intervalId = setInterval(() => { void recreateProofPayload(); }, 15000);
    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [tonWallet, tonConnectUI, publishProof, recreateProofPayload]);

  const clearChallenge = useCallback(() => {
    challengeRef.current = null;
    publishedRequestRef.current = null;
  }, []);

  return {
    challengeRef,
    clearChallenge,
    recreateProofPayload,
  };
}
